import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Space,
  Spin,
  Statistic,
  Typography,
} from 'antd';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GREEN2, GREEN_LIGHT, RED1, RED_LIGHT } from '../../../constants/color';
import { CreditLimit } from '../../../types';
import { getDataWithAuthToken } from '../../../utils/axiosRequest';
import getDashboardStyle from '../../../utils/getDashboardStyle';
import CreditLimitDetailModal from '../CreditLimitDetailModal';

type UserCreditLimitProps = {
  userId?: number;
  userName?: string;
  // 下单弹窗按店铺过滤未付；用户详情页不传，看整体
  sellerId?: number;
  creditLimit?: CreditLimit;
  setCreditLimit: Dispatch<SetStateAction<CreditLimit | undefined>>;
  creditLimitLoading: boolean;
  setCreditLimitLoading: Dispatch<SetStateAction<boolean>>;
  // 是否显示「查看详情」(账龄分档) 入口
  showDetail?: boolean;
  // 精简单行版 (POS 等窄面板)：同样含额度/未发货/已发货/未付款
  compactUI?: boolean;
};

/**
 * 批发账期信用额度：单独突出显示（仅批发 appType）
 * 用户详情页与添加订单弹窗共用此模块；数据/加载状态由父组件持有
 */
const UserCreditLimit = ({
  userId,
  userName,
  sellerId,
  creditLimit,
  setCreditLimit,
  creditLimitLoading,
  setCreditLimitLoading,
  showDetail,
  compactUI,
}: UserCreditLimitProps) => {
  const { t } = useTranslation();
  const isSubscribed = useRef(true);
  const [detailVisible, setDetailVisible] = useState(false);

  useEffect(() => {
    return () => {
      isSubscribed.current = false;
    };
  }, []);

  useEffect(() => {
    if (getDashboardStyle().isWholeSaleAppType && userId) {
      setCreditLimitLoading(true);
      getDataWithAuthToken('users/credit_limit', {
        params: { userId: userId, sellerId: sellerId },
      })
        .then((response) => {
          if (isSubscribed.current) {
            setCreditLimit(
              response && response.goodStatus && response.data
                ? response.data
                : undefined
            );
          }
        })
        .catch((err) => console.log(err))
        .finally(() => {
          if (isSubscribed.current) {
            setCreditLimitLoading(false);
          }
        });
    } else {
      setCreditLimit(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, sellerId]);

  if (!getDashboardStyle().isWholeSaleAppType) {
    return null;
  }
  if (!creditLimit && !creditLimitLoading) {
    return null;
  }

  // Slim single-box variant for narrow panels (POS): same four figures inline
  if (compactUI) {
    const figures: { label: string; value: React.ReactNode; strong?: boolean }[] =
      creditLimit
        ? [
            {
              label: t('users.userListColumns.creditLimit'),
              value: creditLimit.unlimited
                ? String(t('users.userListColumns.unlimited'))
                : creditLimit.creditLimit,
            },
            {
              label: t('users.userListColumns.unshippedAmount'),
              value: creditLimit.unshippedAmount,
            },
            {
              label: t('users.userListColumns.shippedAmount'),
              value: creditLimit.shippedAmount,
              strong: true,
            },
            {
              label: t('users.userListColumns.totalUnpaidAmount'),
              value: creditLimit.unpaidAmount,
              strong: true,
            },
          ]
        : [];
    return (
      <Spin spinning={creditLimitLoading}>
        {creditLimit && (
          <div
            style={{
              marginTop: 8,
              padding: '6px 10px',
              borderRadius: 6,
              border: `1px solid ${creditLimit.warning ? RED1 : GREEN2}`,
              background: creditLimit.warning ? RED_LIGHT : GREEN_LIGHT,
              fontSize: 12,
            }}
          >
            <Space size={12} wrap>
              {figures.map((f) => (
                <span key={f.label}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {`${f.label}: `}
                  </Typography.Text>
                  <Typography.Text
                    strong={f.strong}
                    style={{
                      fontSize: 12,
                      color: f.strong && creditLimit.warning ? RED1 : undefined,
                    }}
                  >
                    {f.value}
                  </Typography.Text>
                </span>
              ))}
              {showDetail && (
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  onClick={() => setDetailVisible(true)}
                >
                  {t('actionsColumn.view')}
                </Button>
              )}
            </Space>
            {creditLimit.warning && (
              <div style={{ marginTop: 4 }}>
                <Space size={4}>
                  <ExclamationCircleOutlined style={{ color: RED1 }} />
                  <Typography.Text type="danger" style={{ fontSize: 12 }}>
                    {creditLimit.warning}
                  </Typography.Text>
                </Space>
              </div>
            )}
          </div>
        )}
        {showDetail && (
          <CreditLimitDetailModal
            userId={userId}
            userName={userName}
            sellerId={sellerId}
            visible={detailVisible}
            setVisible={setDetailVisible}
          />
        )}
      </Spin>
    );
  }

  return (
    <Spin spinning={creditLimitLoading}>
      {creditLimit && (
        <Card
          size="small"
          bordered={false}
          style={{
            marginTop: 16,
            borderRadius: 8,
            border: `2px solid ${creditLimit.warning ? RED1 : GREEN2}`,
            background: creditLimit.warning ? RED_LIGHT : GREEN_LIGHT,
          }}
          bodyStyle={{ padding: '16px 24px' }}
        >
          <Row gutter={[48, 16]} align="middle">
            <Col>
              <Statistic
                title={t('users.userListColumns.creditLimit')}
                value={
                  creditLimit.unlimited
                    ? String(t('users.userListColumns.unlimited'))
                    : creditLimit.creditLimit
                }
                valueStyle={{ fontWeight: 600 }}
              />
            </Col>
            <Col>
              <Statistic
                title={t('users.userListColumns.unshippedAmount')}
                value={creditLimit.unshippedAmount}
              />
            </Col>
            <Col>
              <Statistic
                title={t('users.userListColumns.shippedAmount')}
                value={creditLimit.shippedAmount}
                valueStyle={{
                  fontWeight: 700,
                  color: creditLimit.warning ? RED1 : GREEN2,
                }}
              />
            </Col>
            <Col>
              <Statistic
                title={t('users.userListColumns.totalUnpaidAmount')}
                value={creditLimit.unpaidAmount}
              />
            </Col>
            {showDetail && (
              <Col flex="auto" style={{ textAlign: 'right' }}>
                <Button type="link" onClick={() => setDetailVisible(true)}>
                  {t('actionsColumn.view')}
                </Button>
              </Col>
            )}
          </Row>
          {creditLimit.warning && (
            <Alert
              style={{ marginTop: 12 }}
              type="error"
              showIcon
              message={creditLimit.warning}
            />
          )}
        </Card>
      )}
      {showDetail && (
        <CreditLimitDetailModal
          userId={userId}
          userName={userName}
          sellerId={sellerId}
          visible={detailVisible}
          setVisible={setDetailVisible}
        />
      )}
    </Spin>
  );
};

export default UserCreditLimit;
